import {
  Scene,
  PerspectiveCamera,
  WebGLRenderer,
  AmbientLight,
  PointLight,
  HemisphereLight,
  Color,
  Group,
} from 'three';
import { COLORS, VISUAL } from '../constants';

export class SceneManager {
  readonly scene: Scene;
  readonly camera: PerspectiveCamera;
  readonly renderer: WebGLRenderer;

  // Scene groups for organization
  readonly platformGroup: Group;
  readonly agentsGroup: Group;
  readonly taskQueueGroup: Group;
  readonly environmentGroup: Group;

  private container: HTMLElement;

  constructor(container: HTMLElement) {
    this.container = container;

    // Scene
    this.scene = new Scene();
    this.scene.background = new Color(COLORS.void);

    // Camera
    this.camera = new PerspectiveCamera(
      60,
      container.clientWidth / container.clientHeight,
      0.1,
      1000
    );
    this.camera.position.set(0, VISUAL.cameraHeight, VISUAL.cameraDistance);
    this.camera.lookAt(0, 0, 0);

    // Renderer
    this.renderer = new WebGLRenderer({
      antialias: true,
      alpha: false,
      powerPreference: 'high-performance',
    });
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.toneMapping = 3; // ACESFilmicToneMapping
    this.renderer.toneMappingExposure = 1.0;
    container.appendChild(this.renderer.domElement);

    // Scene groups
    this.platformGroup = new Group();
    this.agentsGroup = new Group();
    this.taskQueueGroup = new Group();
    this.environmentGroup = new Group();

    this.scene.add(this.platformGroup);
    this.scene.add(this.agentsGroup);
    this.scene.add(this.taskQueueGroup);
    this.scene.add(this.environmentGroup);

    // Lighting
    this.setupLighting();

    // Resize handler
    window.addEventListener('resize', this.onResize);
  }

  private setupLighting(): void {
    const ambient = new AmbientLight(0x111122, 0.5);
    this.scene.add(ambient);

    const centerLight = new PointLight(COLORS.healthy, 1.2, 25);
    centerLight.position.set(0, 3, 0);
    this.scene.add(centerLight);

    const hemi = new HemisphereLight(0x1a1a3e, 0x08080d, 0.3);
    this.scene.add(hemi);
  }

  render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  private onResize = (): void => {
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
  };

  dispose(): void {
    window.removeEventListener('resize', this.onResize);
    this.renderer.dispose();
  }
}
